import { z } from "zod";
import type { DocumentCurrency } from "@/shared/lib/performance-document-currency";

export const PerformanceReportDateRangeSchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const PerformanceReportSummaryQuerySchema =
  PerformanceReportDateRangeSchema.extend({
    groupBy: z.enum(["day", "week", "month"]).default("day"),
  });

export const PerformanceEventReportQuerySchema =
  PerformanceReportDateRangeSchema.extend({
    status: z.enum(["draft", "confirmed", "completed", "cancelled"]).optional(),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  });

export const PerformancePayoutReportQuerySchema =
  PerformanceReportDateRangeSchema.extend({
    performerId: z.string().optional(),
    status: z
      .enum(["draft", "pending_approval", "approved", "paid", "cancelled"])
      .optional(),
    eventId: z.string().optional(),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  });

export const PerformanceRevenueReportQuerySchema =
  PerformanceReportDateRangeSchema.extend({
    documentType: z.enum(["quotation", "invoice", "receipt"]).optional(),
    eventId: z.string().optional(),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  });

export const PerformanceRequestReportQuerySchema =
  PerformanceReportDateRangeSchema.extend({
    status: z
      .enum(["new", "negotiating", "pending_quote", "won", "lost", "cancelled"])
      .optional(),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  });

export const PerformancePerformerReportQuerySchema =
  PerformanceReportDateRangeSchema.extend({
    performerId: z.string().optional(),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  });

export type PerformanceReportSummaryQueryDTO = z.infer<
  typeof PerformanceReportSummaryQuerySchema
>;
export type PerformanceEventReportQueryDTO = z.infer<
  typeof PerformanceEventReportQuerySchema
>;
export type PerformancePayoutReportQueryDTO = z.infer<
  typeof PerformancePayoutReportQuerySchema
>;
export type PerformanceRevenueReportQueryDTO = z.infer<
  typeof PerformanceRevenueReportQuerySchema
>;
export type PerformanceRequestReportQueryDTO = z.infer<
  typeof PerformanceRequestReportQuerySchema
>;
export type PerformancePerformerReportQueryDTO = z.infer<
  typeof PerformancePerformerReportQuerySchema
>;

/** Money totals grouped by document currency — never sum across currencies. */
export type CurrencyAmountBuckets = Record<DocumentCurrency, number>;

export type PerformanceReportSummaryDTO = {
  events: {
    total: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    draft: number;
  };
  /** Events in range with ≥2 shows on the same calendar day */
  sameDayDiscountEvents: number;
  /** Show slots billed at additionalShowPercent (same-day 2+) */
  sameDayDiscountedShows: number;
  quoted: CurrencyAmountBuckets;
  invoiced: CurrencyAmountBuckets;
  revenueCollected: CurrencyAmountBuckets;
  outstandingAr: CurrencyAmountBuckets;
  payoutPaid: number;
  payoutOutstanding: number;
  trend: PerformanceReportTrendItemDTO[];
};

export type PerformanceReportTrendItemDTO = {
  period: string;
  revenue: CurrencyAmountBuckets;
  payoutPaid: number;
};

export type PerformanceEventReportRowDTO = {
  id: string;
  title: string;
  eventDate: string;
  locationText: string | null;
  status: string;
  assignmentCount: number;
  quoted: CurrencyAmountBuckets;
  revenue: CurrencyAmountBuckets;
  payoutPaid: number;
  /** LAK revenue − payout only; null when no LAK revenue to compare */
  margin: number | null;
  marginPercent: number | null;
  /** Per-currency revenue − quoted */
  quotedVsActual: CurrencyAmountBuckets;
  /** LAK-only fulfillment; null when LAK quoted is 0 */
  quotedFulfillmentPercent: number | null;
};

export type PerformanceEventReportDTO = {
  data: PerformanceEventReportRowDTO[];
  total: number;
};

export type PerformancePayoutReportSummaryDTO = {
  byStatus: {
    draft: number;
    pendingApproval: number;
    approved: number;
    paid: number;
    cancelled: number;
  };
  topPerformers: Array<{
    performerId: string;
    performerName: string;
    totalPaid: number;
  }>;
};

export type PerformancePayoutReportRowDTO = {
  id: string;
  payoutNo: string;
  performerId: string;
  performerName: string;
  eventId: string;
  eventTitle: string;
  eventDate: string;
  status: string;
  totalAmount: number;
  paidAt: string | null;
};

export type PerformancePayoutReportDTO = {
  summary: PerformancePayoutReportSummaryDTO;
  data: PerformancePayoutReportRowDTO[];
  total: number;
};

export type PerformanceRevenueReportSummaryDTO = {
  quoted: CurrencyAmountBuckets;
  invoiced: CurrencyAmountBuckets;
  collected: CurrencyAmountBuckets;
  outstandingAr: CurrencyAmountBuckets;
};

export type PerformanceRevenueReportRowDTO = {
  id: string;
  documentNo: string;
  documentType: string;
  eventId: string;
  eventTitle: string;
  issuedAt: string | null;
  currency: DocumentCurrency;
  grandTotal: number;
};

export type PerformanceRevenueReportDTO = {
  summary: PerformanceRevenueReportSummaryDTO;
  data: PerformanceRevenueReportRowDTO[];
  total: number;
};

export type PerformanceRequestReportSummaryDTO = {
  total: number;
  byStatus: {
    new: number;
    negotiating: number;
    pendingQuote: number;
    won: number;
    lost: number;
    cancelled: number;
  };
  convertedCount: number;
  conversionRate: number;
  avgDaysToConvert: number | null;
  openOverdueCount: number;
};

export type PerformanceRequestReportRowDTO = {
  id: string;
  title: string;
  contactName: string;
  channel: string;
  status: string;
  createdAt: string;
  convertedAt: string | null;
  convertedEventId: string | null;
  daysToConvert: number | null;
};

export type PerformanceRequestReportDTO = {
  summary: PerformanceRequestReportSummaryDTO;
  data: PerformanceRequestReportRowDTO[];
  total: number;
};

export type PerformancePerformerReportSummaryDTO = {
  activeWithAssignments: number;
  idlePerformers: number;
  totalAssignments: number;
};

export type PerformancePerformerReportRowDTO = {
  performerId: string;
  performerName: string;
  status: string;
  assignmentCount: number;
  eventCount: number;
  payoutPaid: number;
  payoutCommitted: number;
};

export type PerformancePerformerReportDTO = {
  summary: PerformancePerformerReportSummaryDTO;
  performers: PerformancePerformerReportRowDTO[];
  total: number;
};
